<template>
  <div class="l-main">
    <div class="l-main__ctn u-box">
      <div class="c-page-header">
        <div>
          <h2 class="c-page-header__title">
            Configuration settings
          </h2>
          <p class="c-page-header__subtitle">
            Settings available to websites, grouped by section and subsection.
          </p>
        </div>

        <div
          v-if="isDeveloper()"
          class="c-page-header__actions"
        >
          <button
            type="button"
            class="ui-btn ui-btn--primary"
            @click="createOrUpdateOnlineConfiguration()"
          >
            <app-icon name="add" />
            Add setting
          </button>
        </div>
      </div>

      <div class="c-page-toolbar">
        <div class="ui-field c-page-toolbar__search">
          <div class="ui-field__control ui-field__control--with-action">
            <app-icon
              class="ui-field__icon"
              name="search"
            />
            <input
              v-model="term"
              class="ui-field__input"
              type="text"
              placeholder="Search by key, type or section…"
              aria-label="Search settings"
            >
            <button
              v-if="term"
              type="button"
              class="ui-field__action"
              title="Clear search"
              aria-label="Clear search"
              @click="term = ''"
            >
              <app-icon name="close" />
            </button>
          </div>
        </div>

        <span class="c-page-toolbar__meta">
          {{ totalLabel }}
        </span>
      </div>

      <div class="c-table-wrapper c-table--online-configurations c-config-settings-table">
        <base-table-component
          :prop-data="paginatedData"
          :empty-title="term ? 'No settings found' : 'No settings yet'"
          :empty-description="term ? `Nothing matches “${term}”.` : 'Add a setting to make it available to websites.'"
        >
          <template v-slot:default="slotProps">
            <span class="c-table__btn-wrap">
              <generic-button
                v-if="isDeveloper()"
                icon
                transparent
                text-inherit
                @click="createOrUpdateOnlineConfiguration(slotProps.selected.onlineConfiguration)"
              >
                <md-tooltip md-direction="top"> Edit </md-tooltip>
                <img src="../../../../../assets/edit-button.svg" alt="">
              </generic-button>
            </span>

            <span class="c-table__btn-wrap">
              <generic-button
                v-if="isDeveloper()"
                icon
                transparent
                text-inherit
                @click="openDeleteModal(slotProps.selected.onlineConfiguration)"
              >
                <md-tooltip md-direction="top"> Delete </md-tooltip>
                <img src="../../../../../assets/delete-button.svg" alt="">
              </generic-button>
            </span>
          </template>
        </base-table-component>
      </div>
    </div>
  </div>
</template>

<script>

import { mapActions, mapGetters } from 'vuex'
import BaseTableComponent from '../../../../../shared/table/BaseTableComponent'
import DeleteModalComponent from '../../../../../shared/delete-modal/DeleteModalComponent'
import CreateOnlineConfigurationModal from './create'
import get from 'lodash/get'
import GenericButton from '../../../../../shared/buttons/GenericButton'
import AppIcon from '../../../../../shared/app-icon/AppIcon'
import { onlinePermissionsMixin } from "@/mixins/onlinePermissionsMixin";

export default {
  name: "OnlineConfigurationsList",

  components: {
    BaseTableComponent,
    GenericButton,
    AppIcon,
  },

  mixins: [onlinePermissionsMixin],

  data() {
    return {
      currentPage: 1,
      limitPerPage: 20,
      term: '',
    };
  },

  computed: {
    ...mapGetters('account', ['user']),
    ...mapGetters('onlineConfiguration', ['configurations', 'sections', 'subsections', 'types']),

    tableData() {
      return this.configurations.map(onlineConfiguration => ({
        onlineConfiguration: onlineConfiguration,
        rowData: {
          key: onlineConfiguration.key,
          type: this.types.find(type => type.id === onlineConfiguration.typeId)?.name,
          section: this.sections.find(section => section.id === onlineConfiguration.sectionId)?.name,
          subsection: this.subsections.find(subsection => subsection.id === onlineConfiguration.subsectionId)?.name,
        }
      }));
    },

    // case-insensitive match on any visible column
    paginatedData() {
      const term = this.term.trim().toLowerCase();

      if (!term) {
        return this.tableData;
      }

      return this.tableData.filter(({ rowData }) => Object.values(rowData)
        .some(value => String(value || '').toLowerCase().includes(term)));
    },

    totalLabel() {
      const shown = this.paginatedData.length;
      const total = this.tableData.length;
      const noun = total === 1 ? 'setting' : 'settings';

      return this.term ? `${shown} of ${total} ${noun}` : `${total} ${noun}`;
    },

    shouldShowPagination() {
      return this.tableData.length > this.limitPerPage;
    },

  },

  async created() {
    await Promise.all([
      this.getOnlineConfigurationSections(),
      this.getOnlineConfigurationSubsections(),
      this.getOnlineConfigurationTypes(),
    ])

    await this.getOnlineConfiguration();
  },

  methods: {
    ...mapActions('onlineConfiguration', [
      'getOnlineConfiguration',
      'getOnlineConfigurationSections',
      'getOnlineConfigurationSubsections',
      'getOnlineConfigurationTypes',
      'deleteOnlineConfiguration',
    ]),

    goToHome() {
      this.$router.push({ name: 'home' });
    },

    openDeleteModal(selected) {
      this.$modal.show(
          DeleteModalComponent,
          {
            deleteMsg: selected.key,
            propData: selected.id,
            deleteFunction: this.deleteOnlineConfiguration,
            getDataFunction: this.getOnlineConfiguration,
            pageData: {}
          },
          { height: "auto", scrollable: false, width: "440px" }
      );
    },

    createOrUpdateOnlineConfiguration(onlineConfiguration = {}) {
      this.$modal.show(
          CreateOnlineConfigurationModal,
          {
            onlineConfiguration,
            onlineConfigurationId: get(onlineConfiguration, 'id'),
          },
          { height: "auto", scrollable: true }
      );
    },

  },
};
</script>

<style lang="scss" scoped>
.c-config-settings-table ::v-deep {
  // key: identifier, give it room and keep it on one line
  .c-table__cell:first-child {
    width: 38% !important;
    font-family: 'SFMono-Regular', Menlo, Consolas, monospace;
    font-size: 13px;
    white-space: nowrap;
  }

  // type: form field component name
  .c-table__body .c-table__cell:nth-child(2) {
    font-family: 'SFMono-Regular', Menlo, Consolas, monospace;
    font-size: 12px;
    color: var(--text-secondary);
    white-space: nowrap;
  }

  // subsection (web / mobile / global) as a small pill
  .c-table__body .c-table__cell:nth-child(4) span:not(:empty) {
    display: inline-flex;
    align-items: center;
    height: 22px;
    padding: 0 8px;
    border: 1px solid var(--border-default);
    border-radius: var(--radius-full);
    background-color: var(--bg-subtle);
    color: var(--text-secondary);
    font-size: 12px;
    font-weight: 500;
  }
}
</style>
